import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronUp, Receipt, Upload } from "lucide-react";
import { toast } from "sonner";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const CATEGORIES = ["Bakım/Onarım", "Ceza", "Sigorta", "Vergi", "Kırtasiye", "Temizlik", "Kira", "Diğer"];

const empty = () => ({
  date: new Date().toISOString().slice(0,10),
  category: "", description: "", amount: "", month: "", notes: "",
});

export default function ExtraExpensesPage() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRows, setBulkRows] = useState([empty()]);

  const load = async () => {
    try {
      const { data } = await api.get("/extra-expenses");
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  // group by month
  const groups = {};
  items.forEach(r => {
    const m = (r.month || (r.date || "").slice(0,7) || "unknown");
    groups[m] = groups[m] || { month: m, items: [], total: 0, count: 0, by_category: {} };
    groups[m].items.push(r);
    groups[m].total += Number(r.amount) || 0;
    groups[m].count += 1;
    const c = r.category || "Diğer";
    groups[m].by_category[c] = (groups[m].by_category[c] || 0) + (Number(r.amount) || 0);
  });
  const monthList = Object.values(groups).sort((a,b) => b.month.localeCompare(a.month));
  const grand = { total: items.reduce((a,r) => a + (Number(r.amount) || 0), 0), count: items.length };

  const toggle = (m) => {
    const s = new Set(expanded);
    if (s.has(m)) s.delete(m); else s.add(m);
    setExpanded(s);
  };

  const openCreate = () => { setEditing(null); setForm(empty()); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, amount: r.amount }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      category: form.category,
      description: form.description,
      amount: Number(form.amount) || 0,
      month: form.month || (form.date || "").slice(0,7),
      notes: form.notes || "",
    };
    try {
      if (editing) await api.put(`/extra-expenses/${editing.id}`, payload);
      else await api.post("/extra-expenses", payload);
      toast.success("Kaydedildi");
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Ekstra gideri silmek istiyor musunuz?")) return;
    try { await api.delete(`/extra-expenses/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const submitBulk = async () => {
    const valid = bulkRows.filter(r => r.date && r.description && Number(r.amount) > 0);
    if (valid.length === 0) { toast.error("En az bir geçerli satır girin"); return; }
    try {
      for (const r of valid) {
        await api.post("/extra-expenses", {
          date: r.date, category: r.category, description: r.description,
          amount: Number(r.amount), month: r.month || (r.date || "").slice(0,7),
          notes: r.notes || "",
        });
      }
      toast.success(`${valid.length} ekstra gider eklendi`);
      setShowBulk(false); setBulkRows([empty()]); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <>
      <PageHeader
        title="Ekstra Giderler"
        subtitle="Bakım, ceza, sigorta gibi giderler. Aylık kazançtan otomatik düşülür."
        testid="extra-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="extra-bulk-btn" onClick={() => { setBulkRows([empty()]); setShowBulk(true); }} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Upload className="w-3.5 h-3.5" /> Toplu Ekle
            </button>
            <button data-testid="extra-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Gider
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Ekstra Gider</div>
          <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(grand.total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kayıt Sayısı</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(grand.count)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Aylık Ortalama</div>
          <div className="num font-bold text-xl mt-1">{formatTL(monthList.length ? grand.total / monthList.length : 0)}</div>
        </div>
      </div>

      <div className="space-y-3">
        {monthList.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
            <Receipt className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            Henüz ekstra gider kaydı yok.
          </div>
        ) : monthList.map(g => {
          const isOpen = expanded.has(g.month);
          const cats = Object.entries(g.by_category).sort((a,b) => b[1] - a[1]);
          return (
            <div key={g.month} className="bg-white border border-[#E5E5E5] rounded-md">
              <button onClick={() => toggle(g.month)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                  <div>
                    <div className="font-heading font-bold text-lg">{monthLabel(g.month)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5"><span className="num font-semibold">{g.count}</span> gider · {cats.slice(0,3).map(([c]) => c).join(", ")}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam</div>
                  <div className="num font-bold text-base text-[#DC2626]">{formatTL(g.total)}</div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[#F5F5F5] p-4 space-y-4">
                  {cats.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Kategori Kırılımı</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {cats.map(([c, v]) => (
                          <div key={c} className="border border-[#F5F5F5] p-2 text-xs bg-[#FAFAFA]">
                            <div className="font-semibold truncate">{c}</div>
                            <div className="num font-bold mt-1">{formatTL(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA]">
                        <tr>
                          {["Tarih","Kategori","Açıklama","Tutar",""].map((h, i) => (
                            <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map(r => (
                          <tr key={r.id} className="border-t border-[#F5F5F5]">
                            <td className="px-3 py-2 num">{formatDate(r.date)}</td>
                            <td className="px-3 py-2">{r.category || "-"}</td>
                            <td className="px-3 py-2 text-neutral-700">{r.description}</td>
                            <td className="px-3 py-2 text-right num font-semibold text-[#DC2626]">{formatTL(r.amount)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-1">
                                <button onClick={() => openEdit(r)} className="p-1 hover:bg-[#F5F5F5]"><Pencil className="w-3 h-3" /></button>
                                <button onClick={() => remove(r)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Ekstra Gider</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                <input list="cat-list" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input mt-1.5" />
                <datalist id="cat-list">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama *</label>
                <input required value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input type="number" step="any" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Dönem (YYYY-MM)</label>
                <input type="month" value={form.month} onChange={e => setForm({...form, month: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Ekstra Gider</div>
                <div className="font-heading font-bold text-lg">Çoklu Satır Girişi</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-end">
                <button onClick={() => setBulkRows([...bulkRows, empty()])} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  <Plus className="w-3 h-3" /> Satır Ekle
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-[#E5E5E5]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>
                      {["Tarih *","Kategori","Açıklama *","Tutar *",""].map(h => (
                        <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((r, i) => (
                      <tr key={i} className="border-t border-[#F5F5F5]">
                        <td className="px-2 py-2">
                          <input type="date" value={r.date} onChange={e => { const arr = [...bulkRows]; arr[i] = { ...arr[i], date: e.target.value }; setBulkRows(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num" />
                        </td>
                        <td className="px-2 py-2">
                          <input list="cat-list-bulk" value={r.category} onChange={e => { const arr = [...bulkRows]; arr[i] = { ...arr[i], category: e.target.value }; setBulkRows(arr); }} className="w-32 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                          <datalist id="cat-list-bulk">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
                        </td>
                        <td className="px-2 py-2">
                          <input value={r.description} onChange={e => { const arr = [...bulkRows]; arr[i] = { ...arr[i], description: e.target.value }; setBulkRows(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" step="any" value={r.amount} onChange={e => { const arr = [...bulkRows]; arr[i] = { ...arr[i], amount: e.target.value }; setBulkRows(arr); }} className="w-28 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                        </td>
                        <td className="px-2 py-2">
                          {bulkRows.length > 1 && <button onClick={() => setBulkRows(bulkRows.filter((_, idx) => idx !== i))} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button onClick={submitBulk} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Toplu Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
